import { Fragment, useState } from "react";
import { Alert, Badge, Button, Group, Paper, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconClock, IconX } from "@tabler/icons-react";
import type { FeePolicy } from "../../apis/tenantFeePolicy";
import { TenantFeePolicyApi } from "../../apis";
import { formatDate, formatMoney, formatPercent } from "../../utils/format";
import { deductionModeLabel, feeBasisLabel, refundPolicyLabel, scopeLabel } from "../../utils/fee-policy-labels";

interface DiffField {
  label: string;
  before: string;
  after: string;
  changed: boolean;
}

function formatPolicy(p: FeePolicy): Record<string, string> {
  const method =
    p.calculationMethod === "PERCENTAGE" && p.feeRate
      ? `${formatPercent(Number(p.feeRate) * 100)}`
      : p.calculationMethod === "FLAT" && p.flatFeeAmount
      ? `${formatMoney(p.flatFeeAmount, p.feeCurrency)}`
      : p.calculationMethod;
  return {
    "Арга": method,
    "Доод": p.minFeeAmount ? formatMoney(p.minFeeAmount, p.feeCurrency) : "—",
    "Дээд": p.maxFeeAmount ? formatMoney(p.maxFeeAmount, p.feeCurrency) : "—",
    "Тооцох суурь": feeBasisLabel(p.feeBasis),
    "Цуглуулах": deductionModeLabel(p.deductionMode),
    "Буцаалт": refundPolicyLabel(p.refundPolicy),
    "Платформ шимтгэл": p.absorbPlatformFee ? "Тийм" : "Үгүй",
  };
}

function diff(before: FeePolicy, after: FeePolicy): DiffField[] {
  const b = formatPolicy(before);
  const a = formatPolicy(after);
  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)]));
  return keys.map((k) => ({
    label: k,
    before: b[k] ?? "—",
    after: a[k] ?? "—",
    changed: b[k] !== a[k],
  }));
}

interface Props {
  scheduled: FeePolicy;
  /** Currently active version, used to compute diff. */
  active: FeePolicy | null;
  onCanceled: () => void;
  /** Open the read-only detail drawer for the scheduled version. */
  onView?: (p: FeePolicy) => void;
}

export function PolicyScheduledCard({ scheduled, active, onCanceled, onView }: Props) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fields = active ? diff(active, scheduled) : [];

  const onCancel = async () => {
    if (!window.confirm("Хүлээгдэж буй өөрчлөлтийг цуцлах уу?")) return;
    setBusy(true);
    setErr(null);
    try {
      await TenantFeePolicyApi.cancelScheduled(scheduled.id, scheduled.rowVersion);
      onCanceled();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setErr(`Алдаа: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper p="md" withBorder radius="md" style={{ background: "light-dark(var(--mantine-color-cyan-0), var(--mantine-color-dark-6))" }}>
      <Group justify="space-between" align="flex-start" mb="sm">
        <Stack gap={4}>
          <Group gap="xs">
            <Badge color="cyan" leftSection={<IconClock size={12} />}>Товлосон</Badge>
            {scheduled.sourcePolicyId && (
              <Badge color="indigo" variant="light">Засвар</Badge>
            )}
            <Text fw={600}>
              {scopeLabel(scheduled.scope)}{scheduled.name ? ` · ${scheduled.name}` : ""}
            </Text>
            {scheduled.currentVersion ? (
              <Text size="sm" c="dimmed" fw={600}>v{scheduled.currentVersion}</Text>
            ) : null}
          </Group>
          <Text size="xs" c="dimmed">
            {scheduled.effectiveFrom
              ? `${formatDate(scheduled.effectiveFrom)}-нд автоматаар идэвхжинэ`
              : "Идэвхжих огноо тодорхойгүй"}
          </Text>
        </Stack>
        <Group gap="xs">
          {onView && (
            <Button variant="subtle" size="xs" onClick={() => onView(scheduled)}>
              Харах
            </Button>
          )}
          <Button
            variant="light"
            color="red"
            size="xs"
            leftSection={<IconX size={14} />}
            loading={busy}
            onClick={onCancel}>
            Цуцлах
          </Button>
        </Group>
      </Group>

      {err && (
        <Alert color="red" mb="sm">{err}</Alert>
      )}

      {fields.length > 0 && (
        <SimpleGrid cols={3} spacing="xs">
          <Text size="xs" c="dimmed" fw={600}>Талбар</Text>
          <Text size="xs" c="dimmed" fw={600}>Одоо</Text>
          <Text size="xs" c="dimmed" fw={600}>Төлөвлөсөн</Text>
          {fields.map((f) => (
            <Fragment key={f.label}>
              <Text size="sm">{f.label}</Text>
              <Text size="sm">{f.before}</Text>
              <Text
                size="sm"
                c={f.changed ? "cyan.7" : undefined}
                fw={f.changed ? 600 : undefined}>
                {f.after}
              </Text>
            </Fragment>
          ))}
        </SimpleGrid>
      )}
    </Paper>
  );
}
